import { useFormik } from 'formik';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';
import { NAME_REGEX, phoneRegExp } from '@/modules/common/utils/regex.ts';
import { OnboardingWrapper } from '@/modules/entity/components/OnboardingWrapper.tsx';
import {
  useEntityItemUncachedQuery,
  useUpdateEntityMutation,
} from '@/modules/entity/queries/entity.tsx';
import { OnboardingStepProps } from '@/modules/entity/utils/stepper/roadsTypes.ts';
import { CustomCheckbox, TextInputField } from 'ui';
import { PlusIcon, TrashIcon } from 'ui/icons';

interface JointlyDetails {
  contact_name: string;
  phone_number: string;
  email: string;
  additional_emails: string[];
}

export const validationSchemaJointlyContactDetails = yup.object({
  contact_name: yup
    .string()
    .trim()
    .matches(NAME_REGEX, 'Invalid format, only allowed: letters, apostrophes, spaces, hyphens')
    .required('This field is required'),
  phone_number: yup
    .string()
    .trim()
    .matches(
      phoneRegExp,
      "Phone number doesn't match our format, here are a few examples: +61 412 345 678, 11234567890",
    )
    .required('This field is required'),
  email: yup.string().trim().email('Must be a valid email').required('This field is required'),
  additional_emails: yup
    .array()
    .of(yup.string().required('Enter an email').email('Must be a valid email')),
});

const initialValues: JointlyDetails = {
  contact_name: '',
  phone_number: '',
  email: '',
  additional_emails: [],
};

export const JointlyContactDetails = ({ moveForward, allowSkip }: OnboardingStepProps) => {
  const { id: entity_id = 0 } = useParams();
  const { data } = useEntityItemUncachedQuery(+entity_id, entity_id === 0 || isNaN(+entity_id));

  const generateInitialValues = (): JointlyDetails => {
    return {
      ...initialValues,
      ...data?.entity.individual_entity_details?.jointly,
      contact_name:
        data?.entity.individual_entity_details?.jointly?.contact_name ??
        `${data?.entity.individual_entity_details?.jointly?.first_name ?? ''} ${
          data?.entity.individual_entity_details?.jointly?.last_name ?? ''
        }`.trim(),
    };
  };

  const formik = useFormik<JointlyDetails>({
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema: validationSchemaJointlyContactDetails,
    initialValues: generateInitialValues(),
    onSubmit: async values => {
      if (formik.dirty) {
        await updateEntity({
          ...data?.entity,
          individual_entity_details: {
            ...data?.entity.individual_entity_details,
            jointly: {
              ...data?.entity.individual_entity_details?.jointly,
              ...values,
            },
          },
        });
      }
      moveForward && moveForward();
    },
  });

  const { mutateAsync: updateEntity, isLoading } = useUpdateEntityMutation({
    setErrors: formik.setErrors,
  });

  const handleClickPlusButton = () => {
    if (formik.values.additional_emails) {
      formik.values.additional_emails.length <= 4 &&
        formik.setFieldValue('additional_emails', [...formik.values.additional_emails, '']);
    }
  };

  const handleClickDeleteButton = () => {
    if (!formik.values.additional_emails) return;

    formik.setFieldValue('additional_emails', [
      ...formik.values.additional_emails.slice(0, formik.values.additional_emails.length - 1),
    ]);
  };

  const handleClickAdditionalEmails = (isChecked: boolean) => {
    isChecked
      ? formik.setFieldValue('additional_emails', [''])
      : formik.setFieldValue('additional_emails', []);
  };

  const TrashButton = (
    <button
      key="trash"
      type="button"
      className="absolute inset-y-0 right-0 flex items-center"
      onClick={handleClickDeleteButton}
    >
      <img src={TrashIcon} alt="delete" />
    </button>
  );

  const AddButton = (
    <button
      key="plus"
      type="button"
      className="absolute inset-y-0 right-[40px] flex items-center"
      onClick={handleClickPlusButton}
    >
      <img src={PlusIcon} alt="add" />
    </button>
  );

  return (
    <OnboardingWrapper
      title="Jointly contact details"
      onClickContinue={formik.handleSubmit}
      onClickSkip={allowSkip ? moveForward : undefined}
      formik={formik}
      isLoading={isLoading}
    >
      <>
        <div className="w-full flex flex-col items-start justify-center space-y-8">
          <TextInputField name="contact_name" label="contact name" />
          <TextInputField name="phone_number" label="contact telephone number" />
          <TextInputField name="email" label="primary email address" />
        </div>
        <CustomCheckbox
          label="Add Additional Email Addresses"
          checked={formik.values.additional_emails.length > 0}
          onChange={e => handleClickAdditionalEmails(e.target.checked)}
        />
        {formik.values.additional_emails.length > 0 && (
          <div className="w-full flex flex-col items-start justify-center mt-[45px]">
            <div className="w-full flex flex-col items-start justify-center space-y-1">
              <p className="text-gray-900 text-[14px] leading-[22px]">Additional Email Addresses</p>
              <p className="text-gray-600 text-[12px] leading-5">
                Up to 5 additional email addresses
              </p>
            </div>
            <div className="w-full flex flex-col items-start justify-center space-y-4 mt-6">
              {formik.values?.additional_emails?.map((_, index: number) => {
                return (
                  <TextInputField
                    name={`additional_emails[${index}]`}
                    key={'additional_emails_' + index}
                    buttons={
                      (formik.values.additional_emails &&
                        formik.values.additional_emails.length - 1) === index
                        ? [AddButton, TrashButton]
                        : []
                    }
                  />
                );
              })}
            </div>
          </div>
        )}
        <p className="text-gray-600 text-[12px] leading-5 mt-10">
          By providing your email address,you elect to receive all communications electronically by
          the Compli One share registry
          <br /> (where legallypermissible).
        </p>
      </>
    </OnboardingWrapper>
  );
};
